import { api } from '../core/client';
import { wirePhone } from '../core/wire';

/** An order row as returned by the ThaliOS Web API (panel Orders list). */
export interface ApiOrderRow {
  id: string;
  kind: 'dine-in' | 'takeaway' | 'delivery';
  who: string;
  /** Dine-in table this order belongs to (empty for takeaway). */
  tableCode?: string;
  /** Home-delivery address (empty for dine-in/takeaway). */
  deliveryAddress?: string;
  amount: number;
  status: 'placed' | 'preparing' | 'ready' | 'served' | 'out' | 'completed' | 'paid' | 'cancel-requested' | 'cancelled';
  /** Customer's reason when a cancellation is requested/approved. */
  cancelReason?: string;
  time: string;
  /** Placed-on calendar date, "yyyy-MM-dd" in IST. `time` is a clock string with no day, so
   *  this is what the panel's period filter reads. Optional: older API builds omit it. */
  isoDate?: string;
  items: {
    qty: number;
    name: string;
    amount: number;
    /** This line's GST rate % — the menu item's own, else the outlet's. Resolved server-side so the
     *  clients don't each re-derive it from the menu. Optional: an older API build omits it. */
    gst?: number;
    /** Whether the line's price already contains the GST. */
    taxInclusive?: boolean;
  }[];
}

interface ApiEnvelope<T> {
  success: boolean;
  message: string | null;
  result: T;
}

/** Body for placing a customer order (cart → order). */
export interface PlaceOrderPayload {
  tableCode: string;
  type: 'dine-in' | 'takeaway' | 'delivery';
  /** The signed-in customer's phone — binds the order to them (scoped list/bill). */
  customerPhone?: string;
  customerName?: string;
  /** Home-delivery details — the server requires only the address when type === 'delivery'. */
  deliveryAddress?: string;
  customerEmail?: string;
  /** Delivery drop-point from the map pin / GPS (0 when not set). */
  latitude?: number;
  longitude?: number;
  items: { name: string; qty: number; price: number }[];
}

/** Fetch a single customer's orders (scoped by phone) — GET /api/order?phone=. */

/** What the customer gets back after placing an order. */
export interface PlacedOrder {
  orderNo: string;
  kot: string;
  etaMinutes: number;
  amount: number;
  status: string;
}

/** Order operations API — GET /api/order (list) + POST /api/order (place). */
export const orderService = {
  list(): Promise<ApiOrderRow[]> {
    return api.get<ApiEnvelope<ApiOrderRow[]>>('/order').then((r) => (Array.isArray(r.data.result) ? r.data.result : []));
  },
  /**
   * The signed-in customer's own orders — the scoped order list / bill.
   *
   * No `phone` parameter any more: the server takes it from the caller's token. It used to be a
   * query parameter with nothing verifying it, so passing someone else's number returned their
   * order history — name, phone, delivery address and GPS drop point included.
   */
  mine(): Promise<ApiOrderRow[]> {
    return api.get<ApiEnvelope<ApiOrderRow[]>>('/order').then((r) => r.data.result);
  },
  place(payload: PlaceOrderPayload): Promise<PlacedOrder> {
    // Normalised here rather than trusting each caller to remember: the auth store holds the
    // number DISPLAY-formatted ("+91 8107971780") and posting that back is rejected.
    const body = { ...payload, customerPhone: wirePhone(payload.customerPhone) };
    return api.post<ApiEnvelope<PlacedOrder>>('/order', body).then((r) => r.data.result);
  },
  /** Notify staff that a table needs a waiter (POST /api/order/call-waiter). */
  callWaiter(tableCode: string): Promise<void> {
    return api.post<ApiEnvelope<unknown>>('/order/call-waiter', null, { params: { table: tableCode } }).then(() => undefined);
  },

  /** Cancel a still-unstarted order outright (only allowed while status is "placed" — the
   *  server refuses this once the kitchen has accepted the ticket). Returns whether it worked. */
  cancel(orderNo: string): Promise<boolean> {
    const no = orderNo.replace(/^#/, '');
    return api
      .post<ApiEnvelope<unknown>>(`/order/${no}/cancel`, null)
      .then((r) => r.data.success !== false)
      .catch(() => false);
  },
  /** Customer requests a cancellation (awaits waiter/admin approval). */
  requestCancel(orderNo: string, reason: string): Promise<void> {
    const no = orderNo.replace(/^#/, '');
    return api.post<ApiEnvelope<unknown>>(`/order/${no}/request-cancel`, { reason }).then(() => undefined);
  },
  /** Waiter/admin approves a pending cancellation → cancelled. */
  approveCancel(orderNo: string): Promise<void> {
    const no = orderNo.replace(/^#/, '');
    return api.post<ApiEnvelope<unknown>>(`/order/${no}/approve-cancel`, null).then(() => undefined);
  },
  /** Waiter/admin declines a pending cancellation → order stays live. */
  rejectCancel(orderNo: string): Promise<void> {
    const no = orderNo.replace(/^#/, '');
    return api.post<ApiEnvelope<unknown>>(`/order/${no}/reject-cancel`, null).then(() => undefined);
  }
};
